import { useEffect, useState } from "react";
import { Ban, Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const maskPhone = (p = "") => (p.length >= 6 ? `${p.slice(0, 2)}${"X".repeat(p.length - 4)}${p.slice(-2)}` : p);

export function RejectRequestedBadge({ lead }) {
  return (
    <span data-testid={`reject-requested-${lead.lead_id}`} title={lead.reject_request?.reason || ""}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
      <Flag className="w-3 h-3"/> Reject Requested
    </span>
  );
}

function RejectRequestModal({ lead, onClose, onDone }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/reject-request-reasons").then(r => setReasons(r.data.reasons || [])).catch(() => {});
  }, []);
  const valid = reason && detail.trim().length >= 10;
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.post(`/leads/${lead.lead_id}/reject-request`, { reason, detailed_reason: detail.trim() });
      toast.success("Reject request sent to admin");
      onDone();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white w-[95vw] max-w-md" data-testid="reject-request-modal" onClick={e => e.stopPropagation()}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Ban className="w-5 h-5 text-red-400"/> Request Case Rejection</DialogTitle></DialogHeader>
        <div className="rounded-lg border border-slate-800 bg-slate-950 p-3 text-sm grid grid-cols-2 gap-2" data-testid="reject-request-lead-info">
          <div className="col-span-2 text-white font-semibold">{lead.name}</div>
          <div className="text-slate-400 font-mono-alt">{maskPhone(lead.phone)}</div>
          <div className="text-slate-400">{lead.district}</div>
          <div className="col-span-2 text-amber-300 text-xs">{lead.loan_type}</div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Reason for Rejection <span className="text-red-400">*</span></label>
          <select data-testid="reject-request-reason" value={reason} onChange={e => setReason(e.target.value)}
            className="mt-1 w-full h-11 bg-slate-950 border border-slate-700 rounded-md px-3 text-sm text-white focus:outline-none focus:border-amber-500">
            <option value="">Select reason…</option>
            {reasons.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Detailed Reason <span className="text-red-400">*</span></label>
          <Textarea data-testid="reject-request-detail" value={detail} onChange={e => setDetail(e.target.value)} maxLength={1000}
            placeholder="Detail ah sollunga... ex: 3 months ah call pannalum edukkala, 4 bank la reject"
            className="mt-1 bg-slate-950 border-slate-700 min-h-[96px]"/>
          <div className={`text-[11px] mt-1 ${detail.trim().length >= 10 ? "text-emerald-400" : "text-slate-500"}`}>{detail.trim().length}/10 characters minimum</div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} data-testid="reject-request-cancel" className="text-slate-300">Cancel</Button>
          <Button onClick={submit} disabled={!valid || busy} data-testid="reject-request-submit" className="bg-red-600 hover:bg-red-500 text-white font-bold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Flag className="w-4 h-4 mr-2"/>} Submit Request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RequestRejectButton({ lead, onDone, className = "" }) {
  const [open, setOpen] = useState(false);
  if (lead.reject_request?.status === "PENDING") return <RejectRequestedBadge lead={lead}/>;
  return (
    <>
      <button type="button" data-testid={`request-reject-btn-${lead.lead_id}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center justify-center gap-1 px-3 min-h-[36px] rounded-full bg-red-600 hover:bg-red-500 text-white text-xs font-bold active:scale-95 transition ${className}`}>
        <Ban className="w-3.5 h-3.5"/> Request Reject
      </button>
      {open && <RejectRequestModal lead={lead} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone && onDone(); }}/>}
    </>
  );
}
